import {
	getBackendErrorMessage,
	isForbiddenApiError,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { createAppQueryCache } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { apiWrapperSetup } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { memo, useEffect, useState } from 'react';

const ApiLayer = ({ children }) => {
	const tDefault = useTranslationDefault();

	const resolveErrorMessage = (error: unknown) => {
		if (isForbiddenApiError(error)) {
			return tDefault('common_api_wrapper.forbidden.message', {
				defaultValue: "You don't have permission to access this resource.",
			});
		}

		// The server's own sentence, used whenever the code has no phrase yet. A failure has to
		// say SOMETHING: i18next echoes an unknown key straight back, so a code that reached
		// production before its Tolgee entry rendered the toast as the literal
		// "translation.error_codes.120248", and a body the guards could not parse rendered
		// "error_codes.undefined" — both of which read as no error message at all.
		const serverMessage = getBackendErrorMessage(error);
		const generic = tDefault(`common_api_wrapper.local.message`);

		if (!isStandardBackendError(error)) {
			return serverMessage ?? generic;
		}

		const key = `error_codes.${error.error?.code}`;
		const phrase = tDefault(key);
		return phrase && !phrase.endsWith(key) ? phrase : serverMessage ?? generic;
	};

	const onError = async (error: unknown) => {
		// eslint-disable-next-line no-console
		console.error(error);

		showToastV2({
			title: resolveErrorMessage(error),
			state: 'error',
		});
	};

	const [queryClient] = useState(
		() =>
			new QueryClient({
				// v5-style cache-level error handling for queries; per-query intent
				// (suppress / custom message) is driven by `meta`.
				queryCache: createAppQueryCache({
					showErrorToast: (message) => showToastV2({ title: message, state: 'error' }),
					resolveGenericMessage: resolveErrorMessage,
				}),
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						refetchOnReconnect: false,
						retry: false,
						retryOnMount: false,
						initialData: undefined,
						gcTime: 0,
					},
					mutations: {
						retry: false,
						onError,
					},
				},
			})
	);

	useEffect(() => {
		apiWrapperSetup({
			commonLocalErrorHandler: onError,
		});
	}, []);

	return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
const Memoized = memo(ApiLayer);

export { Memoized as ApiLayer };
